// Real, animated ELEMENTAL frames (the premium cosmetic tier). Pure Canvas2D, no WebGL.
//
// One noise-driven ring engine renders every element; each element is just a config (colour ramp,
// flow direction, speed, turbulence, sparkle). A ring is sampled in polar space around the avatar,
// driven by 3 octaves of tiling value noise that scroll and drift so it reads as living motion, then
// pushed through the element colour LUT and upscaled with smoothing. Centre + exterior stay
// transparent so the avatar image sits in the middle, wreathed by the effect.
//   flame  -> fire rising in licking tongues (warm)
//   water  -> cool current hugging the rim, heavier drips at the bottom, foam glints
//   ice    -> slow crystalline frost with twinkling sparkles
//   nature -> soft green motes drifting upward
//
// UI-only (outside src/game): requestAnimationFrame + performance.now are fine here. Each loop
// self-terminates the moment its canvas leaves the DOM, so re-rendered chips never leak loops.

import type { FrameEffect } from '../meta/catalog';

const TWO_PI = Math.PI * 2;

// Canvas drawn this many times larger than the avatar so the effect has room to reach outward.
// Chosen so the avatar-to-canvas ratio is constant, i.e. the effect looks identical at every mount
// size (chip, profile hero, shop tile).
const FX_SCALE = 1.8;

// Global feel knobs (one place to tune every element). SPEED_SCALE slows the whole animation (ring
// scroll, sparkle, rays, Maelstrom spin). EMBER_SPEED_SCALE slows particle launch (also narrows how
// far they travel); EMBER_SPREAD_SCALE tightens the particle scatter so they hug the ring more.
const SPEED_SCALE = 0.45;
const EMBER_SPEED_SCALE = 0.6;
const EMBER_SPREAD_SCALE = 0.55;

type Stop = readonly [number, number, number, number, number]; // t, r, g, b, a

// Scattering particles that fly off the ring (embers / droplets / glints) for a lively, realistic look.
interface EmberConfig {
  rate: number; // particles spawned per second
  color: readonly [number, number, number]; // particle rgb
  size: number; // base particle radius (px, at mount scale)
  speed: number; // outward launch speed (px/s)
  spread: number; // random tangential/scatter velocity (px/s)
  gravity: number; // vertical accel (px/s^2): negative rises (fire), positive falls (water)
}

// Rotating rays (the Solar frame): bright spokes emanating from the ring.
interface RayConfig {
  count: number;
  speed: number; // revolutions per second
  length: number; // ray length (px at mount scale)
  width: number; // ray thickness (px at mount scale)
  color: readonly [number, number, number];
}
// Crackling arcs (the Tempest frame): short-lived jagged lightning bolts flicking off the ring.
interface BoltConfig {
  rate: number; // bolts spawned per second
  glow: readonly [number, number, number]; // wide soft halo colour
  core: readonly [number, number, number]; // thin bright core colour
  length: number; // bolt reach (px at mount scale)
}
// Two elements blended and spinning (the Maelstrom frame): half the ring uses this element, half uses
// `other`, and the split rotates. Each half also spawns its own embers.
interface DualConfig {
  other: FrameEffect; // the second element (its LUT + ember are pulled from ELEMENTS)
  spin: number; // revolutions per second of the split
}

interface ElementConfig {
  intensity: number; // 0..1 overall strength (reach + brightness)
  stops: readonly Stop[]; // colour ramp, transparent at t=0
  riseSign: 1 | -1; // +1 = longer tongues at the TOP (rising), -1 = longer at the BOTTOM (dripping)
  speed: number; // time multiplier (fire fast, ice slow)
  flicker: number; // turbulence gain (fire chaotic, water/ice smooth)
  sparkle: number; // 0..1 twinkle amount (ice/water/nature shimmer)
  glow: readonly [number, number, number]; // ambient glow rgb
  ember: EmberConfig; // scattering particles flung off the ring
  rays?: RayConfig; // Solar: rotating sun rays
  bolts?: BoltConfig; // Tempest: crackling lightning arcs
  dual?: DualConfig; // Maelstrom: a second element spinning opposite this one
}

const FIRE_STOPS: readonly Stop[] = [
  [0.0, 0, 0, 0, 0],
  [0.08, 40, 3, 0, 48],
  [0.2, 120, 14, 0, 150],
  [0.38, 198, 40, 2, 210],
  [0.56, 246, 96, 16, 238],
  [0.72, 255, 154, 34, 250],
  [0.86, 255, 212, 90, 255],
  [1.0, 255, 250, 226, 255],
];
// Deep, saturated blue so water reads as rich water (clearly not the pale frost of ice).
const WATER_STOPS: readonly Stop[] = [
  [0.0, 0, 0, 0, 0],
  [0.1, 5, 24, 82, 62],
  [0.28, 10, 72, 184, 180],
  [0.5, 24, 124, 216, 224],
  [0.7, 78, 182, 238, 244],
  [0.86, 162, 220, 250, 250],
  [1.0, 228, 248, 255, 255],
];
// Pale white-cyan FROST (no deep blue), so ice reads as crystalline frost, not blue water. Higher
// alphas than a wispy effect so the frost ring is solid and clearly visible.
const ICE_STOPS: readonly Stop[] = [
  [0.0, 0, 0, 0, 0],
  [0.1, 120, 165, 200, 130],
  [0.3, 180, 218, 240, 210],
  [0.54, 218, 240, 251, 240],
  [0.76, 238, 249, 254, 250],
  [1.0, 253, 254, 255, 255],
];
const NATURE_STOPS: readonly Stop[] = [
  [0.0, 0, 0, 0, 0],
  [0.1, 10, 44, 12, 56],
  [0.3, 34, 124, 34, 172],
  [0.55, 86, 184, 52, 222],
  [0.75, 164, 222, 92, 242],
  [1.0, 226, 250, 200, 255],
];
const ARCANE_STOPS: readonly Stop[] = [
  [0.0, 0, 0, 0, 0],
  [0.1, 42, 10, 72, 62],
  [0.28, 112, 32, 172, 178],
  [0.5, 182, 62, 222, 224],
  [0.7, 230, 122, 242, 242],
  [0.86, 246, 192, 250, 250],
  [1.0, 255, 240, 222, 255],
];
const SUN_STOPS: readonly Stop[] = [
  [0.0, 0, 0, 0, 0],
  [0.08, 90, 34, 0, 64],
  [0.22, 210, 96, 0, 180],
  [0.42, 250, 162, 22, 224],
  [0.62, 255, 206, 74, 244],
  [0.8, 255, 236, 150, 252],
  [1.0, 255, 252, 232, 255],
];
const STORM_STOPS: readonly Stop[] = [
  [0.0, 0, 0, 0, 0],
  [0.16, 22, 44, 96, 52],
  [0.42, 44, 124, 206, 140],
  [0.66, 126, 204, 255, 196],
  [0.85, 206, 236, 255, 224],
  [1.0, 246, 250, 255, 255],
];

// Bold intensities + abundant scattering embers so the effects read richly and feel showy. Higher
// intensity = longer reach + brighter cores + stronger glow; the ember layer flings glowing particles
// off the ring. Solar adds rotating rays, Tempest adds crackling lightning arcs, Maelstrom spins a
// second element opposite the first.
const ELEMENTS: Record<FrameEffect, ElementConfig> = {
  flame: { intensity: 0.86, stops: FIRE_STOPS, riseSign: 1, speed: 1.05, flicker: 1.1, sparkle: 0.06, glow: [185, 60, 0], ember: { rate: 48, color: [255, 178, 74], size: 2.5, speed: 52, spread: 44, gravity: -20 } },
  water: { intensity: 0.74, stops: WATER_STOPS, riseSign: -1, speed: 0.64, flicker: 0.78, sparkle: 0.18, glow: [22, 92, 205], ember: { rate: 24, color: [110, 195, 255], size: 2.1, speed: 32, spread: 22, gravity: 46 } },
  // Ice: pale frost, near-frozen (slow, low turbulence) with heavy crystalline TWINKLE, so it reads as
  // glittering ice rather than the flowing blue water beside it. Bold intensity + solid frost + lots of
  // big glints so it is clearly visible, not faint.
  ice: { intensity: 0.9, stops: ICE_STOPS, riseSign: -1, speed: 0.2, flicker: 0.66, sparkle: 1.0, glow: [175, 218, 248], ember: { rate: 44, color: [240, 251, 255], size: 2.1, speed: 12, spread: 17, gravity: 3 } },
  nature: { intensity: 0.7, stops: NATURE_STOPS, riseSign: 1, speed: 0.54, flicker: 0.9, sparkle: 0.34, glow: [54, 150, 54], ember: { rate: 20, color: [188, 238, 122], size: 2.0, speed: 26, spread: 32, gravity: -12 } },
  arcane: { intensity: 0.78, stops: ARCANE_STOPS, riseSign: 1, speed: 0.8, flicker: 0.95, sparkle: 0.62, glow: [150, 66, 205], ember: { rate: 34, color: [228, 158, 255], size: 2.4, speed: 46, spread: 48, gravity: -14 } },
  sun: { intensity: 0.92, stops: SUN_STOPS, riseSign: 1, speed: 0.92, flicker: 1.0, sparkle: 0.24, glow: [225, 145, 20], ember: { rate: 52, color: [255, 224, 130], size: 2.7, speed: 56, spread: 46, gravity: -8 }, rays: { count: 12, speed: 0.1, length: 22, width: 3, color: [255, 226, 140] } },
  storm: { intensity: 0.42, stops: STORM_STOPS, riseSign: 1, speed: 1.35, flicker: 0.9, sparkle: 0.5, glow: [60, 130, 220], ember: { rate: 20, color: [190, 232, 255], size: 1.8, speed: 44, spread: 54, gravity: 0 }, bolts: { rate: 10, glow: [90, 170, 255], core: [236, 248, 255], length: 28 } },
  duality: { intensity: 0.82, stops: FIRE_STOPS, riseSign: 1, speed: 0.98, flicker: 1.02, sparkle: 0.14, glow: [130, 90, 150], ember: { rate: 34, color: [255, 178, 74], size: 2.4, speed: 50, spread: 42, gravity: -18 }, dual: { other: 'water', spin: 0.16 } },
};

interface Lut {
  r: Uint8Array;
  g: Uint8Array;
  b: Uint8Array;
  a: Uint8Array;
}

function buildLut(stops: readonly Stop[]): Lut {
  const r = new Uint8Array(256);
  const g = new Uint8Array(256);
  const b = new Uint8Array(256);
  const a = new Uint8Array(256);
  for (let s = 0; s < 256; s++) {
    const t = s / 255;
    let k = 0;
    while (k < stops.length - 2 && t > stops[k + 1]![0]) k++;
    const a0 = stops[k]!;
    const a1 = stops[k + 1]!;
    const span = a1[0] - a0[0];
    const f = span > 0 ? (t - a0[0]) / span : 0;
    r[s] = (a0[1] + (a1[1] - a0[1]) * f) | 0;
    g[s] = (a0[2] + (a1[2] - a0[2]) * f) | 0;
    b[s] = (a0[3] + (a1[3] - a0[3]) * f) | 0;
    a[s] = (a0[4] + (a1[4] - a0[4]) * f) | 0;
  }
  return { r, g, b, a };
}

// Colour LUTs are element-constant, so build them once.
const LUTS: Record<FrameEffect, Lut> = {
  flame: buildLut(FIRE_STOPS),
  water: buildLut(WATER_STOPS),
  ice: buildLut(ICE_STOPS),
  nature: buildLut(NATURE_STOPS),
  arcane: buildLut(ARCANE_STOPS),
  sun: buildLut(SUN_STOPS),
  storm: buildLut(STORM_STOPS),
  duality: buildLut(FIRE_STOPS),
};

function fade(t: number): number {
  return t * t * (3 - 2 * t);
}

function runEffect(canvas: HTMLCanvasElement, cssSize: number, avatarRadiusCss: number, cfg: ElementConfig, lut: Lut): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const q = Math.max(0, Math.min(1, cfg.intensity));
  const dpr = Math.max(1, Math.min(2.5, window.devicePixelRatio || 1));
  canvas.width = Math.round(cssSize * dpr);
  canvas.height = Math.round(cssSize * dpr);
  ctx.scale(dpr, dpr);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  const W = 90;
  const H = 90;
  const bufScale = cssSize / W;
  const off = document.createElement('canvas');
  off.width = W;
  off.height = H;
  const octx = off.getContext('2d');
  if (!octx) return;
  const img = octx.createImageData(W, H);
  const data = img.data;

  const CX = W * 0.5;
  const CY = H * 0.5;
  const R0 = avatarRadiusCss / bufScale;

  const reachA = 4.5 + 1.5 * q;
  const reachB = 6 + 7 * q;
  const heatGain = (1.02 + 0.62 * q) * cfg.flicker;
  const dFall = 1.5 - 0.22 * q;
  const baseMul = 0.2 + 0.5 * q;
  const glowA = 0.05 + 0.17 * q;
  const outerR = R0 + reachA + reachB + 3;

  const NU = 64;
  const NV = 256;
  const noise = new Float32Array(NU * NV);
  let seed = 0x9e3779b9 >>> 0;
  for (let i = 0; i < noise.length; i++) {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    noise[i] = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
  const vnoise = (x: number, y: number): number => {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    const xf = fade(x - xi);
    const yf = fade(y - yi);
    const x0 = xi & (NU - 1);
    const x1 = (xi + 1) & (NU - 1);
    const y0 = yi & (NV - 1);
    const y1 = (yi + 1) & (NV - 1);
    const r0 = y0 * NU;
    const r1 = y1 * NU;
    const v00 = noise[r0 + x0]!;
    const v10 = noise[r0 + x1]!;
    const v01 = noise[r1 + x0]!;
    const v11 = noise[r1 + x1]!;
    const a = v00 + (v10 - v00) * xf;
    const b = v01 + (v11 - v01) * xf;
    return a + (b - a) * yf;
  };

  const N = W * H;
  const pxOn = new Uint8Array(N);
  const pxU = new Float32Array(N);
  const pxV = new Float32Array(N);
  const pxLR = new Float32Array(N);
  const pxBand = new Float32Array(N);
  const pxBase = new Float32Array(N);
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const idx = y * W + x;
      const dx = x + 0.5 - CX;
      const dy = y + 0.5 - CY;
      const r = Math.sqrt(dx * dx + dy * dy);
      if (r < R0 - 1.5 || r > outerR) {
        pxOn[idx] = 0;
        continue;
      }
      pxOn[idx] = 1;
      pxU[idx] = ((Math.atan2(dy, dx) + Math.PI) / TWO_PI) * NU;
      pxV[idx] = r * 0.9;
      pxLR[idx] = r - R0;
      const up = (-dy / (r + 1e-4)) * cfg.riseSign; // riseSign flips which side gets longer tongues
      const buoy = 0.5 + 0.5 * up;
      pxBand[idx] = reachA + reachB * buoy;
      const bb = 1 - (r - R0) / 4;
      pxBase[idx] = bb > 0 ? bb : 0;
    }
  }

  // The noise scroll already scales with cfg.speed twice (via riseSpeed AND tSec), so applying
  // SPEED_SCALE to tSec ALONE slows every time-driven layer by exactly SPEED_SCALE (linear), keeping
  // the per-element speed tuning intact.
  const spd = cfg.speed * SPEED_SCALE;
  const riseSpeed: readonly [number, number, number] = [3.0 * cfg.speed, 5.0 * cfg.speed, 8.0 * cfg.speed];
  const driftU: readonly [number, number, number] = [0.8 * cfg.speed, -1.5 * cfg.speed, 2.2 * cfg.speed];
  const sparkle = cfg.sparkle;
  const mid = cssSize / 2;
  const start = performance.now();

  // Ember/particle layer: glowing specks flung off the ring so the effect scatters and feels alive.
  // UI-only, so Math.random is fine here (this file is outside the deterministic engine).
  const ember = cfg.ember;
  const emberScale = bufScale; // size particles relative to the mount size
  const dualCfg = cfg.dual ?? null;
  const emberB = dualCfg ? ELEMENTS[dualCfg.other].ember : null; // second element's embers (Maelstrom)
  const lutB = dualCfg ? LUTS[dualCfg.other] : lut;
  const riseB = dualCfg ? ELEMENTS[dualCfg.other].riseSign : cfg.riseSign;
  const makeEmberSprite = (color: readonly [number, number, number]): HTMLCanvasElement => {
    const c = document.createElement('canvas');
    c.width = 16;
    c.height = 16;
    const g = c.getContext('2d');
    if (g) {
      const grd = g.createRadialGradient(8, 8, 0, 8, 8, 8);
      const [er, egc, eb] = color;
      grd.addColorStop(0, `rgba(${er},${egc},${eb},1)`);
      grd.addColorStop(0.45, `rgba(${er},${egc},${eb},0.55)`);
      grd.addColorStop(1, `rgba(${er},${egc},${eb},0)`);
      g.fillStyle = grd;
      g.fillRect(0, 0, 16, 16);
    }
    return c;
  };
  const emberSprite = makeEmberSprite(ember.color);
  const emberSpriteB = emberB ? makeEmberSprite(emberB.color) : emberSprite;

  interface Particle {
    x: number;
    y: number;
    vx: number;
    vy: number;
    life: number;
    max: number;
    sz: number;
    gy: number; // per-particle vertical accel (fire rises, water falls in Maelstrom)
    useB: boolean; // draw with the second sprite (Maelstrom water half)
  }
  const parts: Particle[] = [];
  const MAX_PARTS = 64;
  let spawnAcc = 0;
  let prevNow = start;
  let spinT = 0; // current rotation phase of the Maelstrom split (updated each frame)
  const spawn = (): void => {
    const a = Math.random() * TWO_PI;
    const cs = Math.cos(a);
    const sn = Math.sin(a);
    // Maelstrom: the spawn angle (vs the spinning split) picks which element and embers this belongs to.
    let em = ember;
    let rs = cfg.riseSign;
    let useB = false;
    if (dualCfg && emberB) {
      let frac = (a + Math.PI) / TWO_PI + spinT;
      frac -= Math.floor(frac);
      if (frac >= 0.5) {
        em = emberB;
        rs = riseB;
        useB = true;
      }
    }
    const launch = em.speed * EMBER_SPEED_SCALE * emberScale;
    const sp = launch * (0.6 + 0.8 * Math.random());
    const spr = em.spread * EMBER_SPREAD_SCALE * emberScale;
    const jit = (Math.random() - 0.5) * 2;
    const life = 0.7 + Math.random() * 0.7;
    parts.push({
      x: mid + cs * avatarRadiusCss,
      y: mid + sn * avatarRadiusCss,
      vx: cs * sp - sn * spr * jit * 0.5 + (Math.random() - 0.5) * spr,
      vy: sn * sp - rs * launch * 0.75 + (Math.random() - 0.5) * spr,
      life,
      max: life,
      sz: em.size * emberScale * (0.7 + 0.6 * Math.random()),
      gy: em.gravity * EMBER_SPEED_SCALE * emberScale,
      useB,
    });
  };

  // Lightning bolts (Tempest): short-lived jagged arcs radiating off the ring, redrawn each frame.
  interface Bolt {
    pts: number[];
    life: number;
    max: number;
  }
  const bolts: Bolt[] = [];
  let boltAcc = 0;
  const spawnBolt = (): void => {
    if (!cfg.bolts) return;
    const a = Math.random() * TWO_PI;
    const cs = Math.cos(a);
    const sn = Math.sin(a);
    const r0 = avatarRadiusCss + 1;
    const r1 = r0 + cfg.bolts.length * emberScale * (0.6 + 0.8 * Math.random());
    const segs = 5;
    const pts: number[] = [];
    for (let i = 0; i <= segs; i++) {
      const t = i / segs;
      const rr = r0 + (r1 - r0) * t;
      const jitter = i === 0 || i === segs ? 0 : (Math.random() - 0.5) * (r1 - r0) * 0.55;
      pts.push(mid + cs * rr - sn * jitter, mid + sn * rr + cs * jitter);
    }
    const life = 0.07 + Math.random() * 0.11;
    bolts.push({ pts, life, max: life });
  };

  const frame = (now: number): void => {
    if (!canvas.isConnected) return;
    const tSec = ((now - start) / 1000) * spd;
    spinT = dualCfg ? dualCfg.spin * tSec : 0;
    const vO0 = tSec * riseSpeed[0];
    const uO0 = tSec * driftU[0];
    const vO1 = tSec * riseSpeed[1];
    const uO1 = tSec * driftU[1];
    const vO2 = tSec * riseSpeed[2];
    const uO2 = tSec * driftU[2];
    // sparkle twinkle uses its OWN clock (element speed only slows the base flow, not the glinting) so a
    // near-frozen ice ring still shimmers; still respects the global SPEED_SCALE.
    const spT = ((now - start) / 1000) * SPEED_SCALE * 1.7;

    for (let p = 0; p < N; p++) {
      const o = p * 4;
      if (!pxOn[p]) {
        data[o + 3] = 0;
        continue;
      }
      const u = pxU[p]!;
      const v = pxV[p]!;
      const n0 = vnoise(u + uO0, v - vO0);
      const n1 = vnoise(u * 2 + uO1, v * 2 - vO1);
      const n2 = vnoise(u * 4 + uO2, v * 4 - vO2);
      const heat = 0.5 * n0 + 0.3 * n1 + 0.2 * n2;
      let d = pxLR[p]! / pxBand[p]!;
      if (d < 0) d = 0;
      const base = pxBase[p]! * (baseMul + 0.85 * heat);
      let inten = heat * heatGain - d * dFall + base;
      if (sparkle > 0 && inten > 0) {
        // Twinkling glints: a high-frequency noise thresholded to bright points that drift over time.
        const sn = vnoise(u * 7 - spT, v * 7 + spT * 0.6);
        if (sn > 0.86) inten += sparkle * ((sn - 0.86) / 0.14);
      }
      if (inten <= 0) {
        data[o + 3] = 0;
        continue;
      }
      if (inten > 1) inten = 1;
      const li = (inten * 255) | 0;
      // Maelstrom: pick the fire or water LUT by the pixel's angle vs the spinning split.
      let cl = lut;
      if (dualCfg) {
        let frac = u / NU + spinT;
        frac -= Math.floor(frac);
        cl = frac < 0.5 ? lut : lutB;
      }
      data[o] = cl.r[li]!;
      data[o + 1] = cl.g[li]!;
      data[o + 2] = cl.b[li]!;
      data[o + 3] = cl.a[li]!;
    }
    octx.putImageData(img, 0, 0);

    ctx.clearRect(0, 0, cssSize, cssSize);
    ctx.globalCompositeOperation = 'lighter';
    const glowOuter = Math.min(mid, avatarRadiusCss + (reachA + reachB) * bufScale + 2);
    const glow = ctx.createRadialGradient(mid, mid, avatarRadiusCss * 0.8, mid, mid, glowOuter);
    const [gr, gg, gb] = cfg.glow;
    glow.addColorStop(0, `rgba(${gr},${gg},${gb},0)`);
    glow.addColorStop(0.5, `rgba(${gr},${gg},${gb},${glowA})`);
    glow.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, cssSize, cssSize);
    ctx.drawImage(off, 0, 0, W, H, 0, 0, cssSize, cssSize);

    // scattering embers (drawn additively over the ring, still in 'lighter' mode)
    const dt = Math.min(0.05, (now - prevNow) / 1000);
    prevNow = now;
    spawnAcc += ember.rate * dt;
    while (spawnAcc >= 1 && parts.length < MAX_PARTS) {
      spawnAcc -= 1;
      spawn();
    }
    for (let i = parts.length - 1; i >= 0; i--) {
      const pt = parts[i]!;
      pt.life -= dt;
      if (pt.life <= 0) {
        parts.splice(i, 1);
        continue;
      }
      pt.vy += pt.gy * dt;
      pt.vx *= 0.985;
      pt.x += pt.vx * dt;
      pt.y += pt.vy * dt;
      const f = pt.life / pt.max;
      ctx.globalAlpha = f * f;
      const s = pt.sz * (0.5 + 0.9 * f) * 3.4;
      ctx.drawImage(pt.useB ? emberSpriteB : emberSprite, pt.x - s / 2, pt.y - s / 2, s, s);
    }
    ctx.globalAlpha = 1;

    // Solar rays: bright spokes rotating around the ring.
    if (cfg.rays) {
      const R = cfg.rays;
      const baseR = avatarRadiusCss + 1;
      const outR = baseR + R.length * emberScale;
      const rot = R.speed * tSec * TWO_PI;
      const [rr, rg, rb] = R.color;
      ctx.lineWidth = Math.max(1, R.width * emberScale);
      ctx.lineCap = 'round';
      for (let i = 0; i < R.count; i++) {
        const a = rot + (i / R.count) * TWO_PI;
        const cs = Math.cos(a);
        const sn = Math.sin(a);
        const x0 = mid + cs * baseR;
        const y0 = mid + sn * baseR;
        const x1 = mid + cs * outR;
        const y1 = mid + sn * outR;
        const g2 = ctx.createLinearGradient(x0, y0, x1, y1);
        g2.addColorStop(0, `rgba(${rr},${rg},${rb},0.55)`);
        g2.addColorStop(1, `rgba(${rr},${rg},${rb},0)`);
        ctx.strokeStyle = g2;
        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.lineTo(x1, y1);
        ctx.stroke();
      }
    }

    // Tempest bolts: spawn + draw crackling arcs (a soft glow pass, then a bright core pass).
    if (cfg.bolts) {
      boltAcc += cfg.bolts.rate * dt;
      while (boltAcc >= 1) {
        boltAcc -= 1;
        spawnBolt();
      }
      const [glr, glg, glb] = cfg.bolts.glow;
      const [cr, cg, cb] = cfg.bolts.core;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      for (let i = bolts.length - 1; i >= 0; i--) {
        const b = bolts[i]!;
        b.life -= dt;
        if (b.life <= 0) {
          bolts.splice(i, 1);
          continue;
        }
        const f = b.life / b.max;
        const trace = (): void => {
          ctx.beginPath();
          ctx.moveTo(b.pts[0]!, b.pts[1]!);
          for (let k = 2; k < b.pts.length; k += 2) ctx.lineTo(b.pts[k]!, b.pts[k + 1]!);
          ctx.stroke();
        };
        ctx.strokeStyle = `rgba(${glr},${glg},${glb},${0.5 * f})`;
        ctx.lineWidth = 3.2 * emberScale;
        trace();
        ctx.strokeStyle = `rgba(${cr},${cg},${cb},${0.95 * f})`;
        ctx.lineWidth = 1.3 * emberScale;
        trace();
      }
    }
    ctx.globalCompositeOperation = 'source-over';

    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
}

/**
 * Turn an `.avatar-frame` element into a live elemental frame: mounts a canvas behind the avatar
 * image and starts the animation. The wrapper must already contain the `.avatar-frame__img`.
 * The caller is responsible for the reduced-motion check (see motionAllowed).
 */
export function attachEffectFrame(wrap: HTMLElement, size: number, kind: FrameEffect): void {
  wrap.classList.add('avatar-frame--fx');
  const cssSize = Math.round(size * FX_SCALE);
  const canvas = document.createElement('canvas');
  canvas.className = 'avatar-fx__canvas';
  canvas.style.width = `${cssSize}px`;
  canvas.style.height = `${cssSize}px`;
  wrap.insertBefore(canvas, wrap.firstChild);
  runEffect(canvas, cssSize, size / 2, ELEMENTS[kind], LUTS[kind]);
}

/** Whether animated canvas frames should run (false under prefers-reduced-motion). */
export function motionAllowed(): boolean {
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
